import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CasinoAllButton, CasinoFilter, CasinoSliderButtons } from '@swf/casino';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import RawButton from '@swf/components/StarsButton/RawButton';
import StarsNav, { Variant as NavVariant } from '@swf/components/StarsNavigation/StarsNavigation';
import Translation from '@swf/components/Translation/Translation';
import Logo from '@swf/core/assets/icons/logo.svg';
import VegasLogo from '@swf/core/assets/icons/vegas-races-logo.svg';
import { Size as ScreenSize } from '@swf/core/enums';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { RaceFilter } from '@swf/core/types/CasinoRaces';
import type { TranslationTag } from '@swf/core/types/i18n';

import {
  TXTWCL_My_Races,
  TXTWCL_Next_Races,
  TXTWCL_Terms_and_Conditions
} from './RaceHeader.messages.json';
import styles from './RaceHeader.scss';

interface Props {
  logo: boolean;
  title?: string;
  helpButton?: string;
  helpTitle?: string;
  help: boolean;
  moreLink: string;
  showFilters?: boolean;
  active?: TranslationTag['id'];
  handleFilter?(filter: RaceFilter): void;
  sliderRef?: React.RefObject<SliderHandles>;
  sliderState?: SliderState;
  slide?: boolean;
}

const filters: RaceFilter[] = [
  {
    tag: TXTWCL_Next_Races,
    service: 'all',
    auth: true
  },
  {
    tag: TXTWCL_My_Races,
    service: 'user',
    auth: true
  }
];

const RaceHeader: React.FC<Props> = ({
  logo,
  title = '',
  helpButton,
  helpTitle,
  help,
  moreLink,
  showFilters,
  active,
  handleFilter,
  sliderRef,
  sliderState,
  slide
}) => {
  const info = logo || help;
  const { screenSize } = useScreenSize();
  const vegasContent = useSelector(select.site.showVegasContent);
  const { modal } = useDispatch<Dispatch>();
  const showMoreButton = !!moreLink && !showFilters;
  const showTitle = !logo && title && title.length;

  const handleHelp = useCallback(() => {
    modal.enqueue({
      path: '/casino/help',
      locationState: {
        buttonText: helpButton,
        title: helpTitle
      }
    });
  }, [helpButton, helpTitle, modal]);

  if (!info && !slide && !showMoreButton) {
    return null;
  }

  return (
    <header
      className={classNames(styles.legacy, styles.header, {
        [styles.row]: !showFilters
      })}
    >
      {!!logo && <div />}
      {info && (
        <div className={classNames(styles.container)}>
          {showTitle > 0 && <span className={styles.title}>{title}</span>}
          {logo &&
            (vegasContent ? (
              <VegasLogo className={styles.logo} />
            ) : (
              <Logo className={styles.logo} />
            ))}
          {help && (
            <RawButton
              size="small"
              variant="casino"
              className={classNames(styles.terms, showTitle && styles.showTitle)}
              onClick={() => handleHelp()}
            >
              {screenSize === ScreenSize.XXSmall ? (
                `T&C`
              ) : (
                <Translation {...TXTWCL_Terms_and_Conditions} />
              )}
            </RawButton>
          )}
        </div>
      )}
      {!logo && <div />}
      {!showFilters && !moreLink && !slide && <div />}
      {showFilters && (
        <StarsNav
          className={classNames(styles.filters, { [styles.info]: info })}
          variant={NavVariant.Transparent}
        >
          {handleFilter &&
            filters.map((filter) => (
              <CasinoFilter
                active={filter.tag.id === active}
                key={filter.tag.id}
                onClick={() => handleFilter(filter)}
                translation={filter.tag}
              />
            ))}
        </StarsNav>
      )}
      {(slide || showMoreButton) && (
        <div className={styles.buttons}>
          {showMoreButton && <CasinoAllButton href={moreLink} />}
          {slide && sliderRef && sliderState && (
            <CasinoSliderButtons sliderRef={sliderRef} sliderState={sliderState} />
          )}
        </div>
      )}
    </header>
  );
};

export default RaceHeader;
